<script setup lang="ts">
const props = defineProps<{
  block: BlockPersonList,
  noHorizontalPadding?: boolean,
}>()

const splitNames = (name?: string | null) => (name || '')
  .split(',')
  .map(part => part.trim())
  .filter(Boolean)

const roleFirst = computed(() => props.block.columnOrder === 'roleFirst')

// Payload hands back `role: ""` (and the odd whitespace-only string) rather
// than dropping the key, so trim before deciding. A list where not a single
// entry carries a role is a plain list of names and flows into two equal
// columns instead of the role/name grid.
const hasRole = (item: BlockPersonList['items'][number]) => !!item.role?.trim()

const hasAnyRole = computed(() => props.block.items.some(hasRole))

const gridColumns = computed(() => hasAnyRole.value
  ? 'grid-cols-[auto_1fr] <sm:grid-cols-1'
  : 'grid-cols-2 <sm:grid-cols-1')

// The role/name grid collapses to a single column below `sm`, so each entry
// needs a gap to the next one, hung off its last cell. The names-only list is
// one line per entry and needs no gap.
const stackSpacing = computed(() => hasAnyRole.value
  ? '<sm:[&:not(:last-child)>*:last-child]:mb-0_5'
  : '')
</script>

<template>
  <section class="container-d my-3" :class="{ 'px-1': !noHorizontalPadding }">
    <h2 v-if="block.label" class="label">
      {{ block.label }}
    </h2>

    <core-rich-text
      v-if="hasRichTextContent(block.intro)"
      :content="block.intro"
      class="mb-1"
    />

    <!-- The li is `display: contents`, so all cells share one grid: every
         entry has to contribute both cells or the columns shift out of step,
         and the column order has to be DOM order — `order-*` would sort the
         cells across the whole grid, not within the entry. -->
    <ul class="text-d grid gap-x-2" :class="gridColumns">
      <li
        v-for="item in block.items"
        :key="item.id"
        class="contents"
        :class="stackSpacing"
      >
        <span v-if="hasAnyRole && roleFirst">{{ item.role }}</span>

        <div class="flex flex-wrap gap-x-[.5ch]">
          <shared-person-link
            v-if="item.internal && item.person"
            :name="item.person.title"
            :slug="item.person.slug"
          />
          <template v-else>
            <span
              v-for="(name, index) in splitNames(item.name)"
              :key="index"
            >
              {{ name }}{{ index < splitNames(item.name).length - 1 ? ',' : '' }}
            </span>
          </template>
        </div>

        <span v-if="hasAnyRole && !roleFirst">{{ item.role }}</span>
      </li>
    </ul>
  </section>
</template>
